/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import * as React from 'react';

import { ErrorBoundaryMessage } from '@/components/error/error-boundary-message';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  customErrorMessage?: string;
  className?: string;
  fallback?: React.ReactNode;
  onError?: (hasError: boolean) => void;
  resetKey?: number;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log error to your service
    this.logErrorToMyService(
      error,
      errorInfo.componentStack ?? undefined
      // Note: React.captureOwnerStack() may not be available in all React versions
      // Remove or conditionally use based on your React version
    );

    // Notify parent that an error occurred
    this.props.onError?.(true);
  }

  componentDidUpdate(
    prevProps: Readonly<ErrorBoundaryProps>,
    prevState: Readonly<ErrorBoundaryState>
  ): void {
    // If resetKey changed, clear the error
    if (
      this.props.resetKey !== undefined &&
      this.props.resetKey !== prevProps.resetKey &&
      this.state.hasError
    ) {
      this.setState({ hasError: false, error: null });
    }

    // If error state changed, notify parent
    if (prevState.hasError !== this.state.hasError) {
      this.props.onError?.(this.state.hasError);
    }
  }

  private logErrorToMyService(error: Error, componentStack?: string): void {
    // Implement your error logging logic here
    console.error('Error caught by ErrorBoundary:');
    console.error('Message:', error.message);
    console.error('Stack:', error.stack);
    console.error('Name:', error.name);

    if (componentStack) {
      console.error('Component stack:', componentStack);
    }

    // You can also access the full error object
    console.error('Full error object:', error);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      // Custom Error UI
      return (
        this.props.fallback || (
          <>
            {this.state.error ? (
              <ErrorBoundaryMessage
                errorMessage={this.state.error.message || 'Unknown error'}
                customErrorMessage={this.props.customErrorMessage}
                className={this.props.className}
              />
            ) : (
              <p className="text-white">Something went wrong.</p>
            )}
          </>
        )
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
